import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Form,
  Formik,
  useFormikContext,
  type FormikHelpers,
  type FormikProps,
} from "formik";
import Text from "../UI/Text";
import Card from "../UI/Card";
import ModalDialog from "../UI/ModalDialog";
// @ts-expect-error pure js
import TabbedForm from "../UI/TabbedForm";
// @ts-expect-error pure js
import Radio from "../UI/Radio";
// @ts-expect-error pure js
import RadioGroup from "../UI/RadioGroup";
// @ts-expect-error pure js
import { GenerateOnly, Lrar, SendMail } from "../UI/Icons";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import BuyCredits from "../Stripe/BuyCredits";
// @ts-expect-error pure js
import { RECOMMANDE_PRICE } from "../../models/signatureElectronique";
// @ts-expect-error pure js
import { ProductType } from "../../utils/constants/stripe";
// @ts-expect-error pure js
import useAccount from "../../hooks/use-account";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
// @ts-expect-error pure js
import useCourriers from "../../hooks/use-courriers";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import useProperties from "../../hooks/use-properties";
import SelectCourrier from "./Actions/SelectCourrier";
import type { Tenant } from "../../models/tenant";
import type { Property } from "../../models/property";
import type { Courrier } from "../../models/courrier";
import type { Profile, ProfileBailleur } from "../Profile/types";
import Callout from "../UI/Callout";
import bulkStyles from "./Bulk/BulkActions.module.css";
import BulkSelectionCard from "./Bulk/BulkSelectionCard";
import BulkPdfPreview from "./Bulk/BulkPdfPreview";
import { usePdfPreviewCache } from "./Bulk/usePdfPreviewCache";
import type { BulkResult, BulkTenantModalProps } from "./Bulk/types";
import { useBulkResultsToast } from "./Bulk/useBulkResultsToast";
import { useBatchSelection } from "./Bulk/useBatchSelection";
import Editor, { type EditorHandle } from "../RichTextEditor/Editor";
import FileInput, { type UploadedFile } from "../UI/FileInput";
import VariablePicker from "../RichTextEditor/VariablePicker";
import {
  getCourrierVariables,
  wrapVariablesAsPills,
} from "../../models/courrierVariables";
import styles from "./BulkCourriersModal.module.css";

type EnvoiType = "mail" | "lrar" | "generer";

interface BulkCourrierAttachment {
  src: string;
  filename?: string;
}

interface BulkCourriersFormValues {
  batchIds: number[];
  courrierStaticId: string;
  courrierContent: string;
  typeEnvoi: EnvoiType;
  files: UploadedFile[];
}

interface TabbedFormHandle {
  resetTab: () => void;
  selectTab: (id: string, isPreselect?: boolean) => void;
  selectNextTab: () => void;
  selectPreviousTab: () => void;
}

const INITIAL_VALUES: Omit<BulkCourriersFormValues, "batchIds"> = {
  courrierStaticId: "",
  courrierContent: "",
  typeEnvoi: "generer",
  files: [],
};

export default function BulkCourriersModal({
  open,
  onOpenChange,
  selectedTenantIds,
  resetSelection,
}: BulkTenantModalProps) {
  const tabbedFormRef = useRef<TabbedFormHandle>(null);
  const formikRef = useRef<FormikProps<BulkCourriersFormValues>>(null);
  const {
    bulkCreateCourrier,
    tenantsById,
  }: {
    bulkCreateCourrier: (params: {
      tenantIds: number[];
      courrierStaticId: string;
      courrierContent: string;
      typeEnvoi: EnvoiType;
      files: BulkCourrierAttachment[];
    }) => Promise<{ results: BulkResult[] }>;
    tenantsById: Record<number, Tenant>;
  } = useTenants({ withById: true });
  const notifyBulkResults = useBulkResultsToast(tenantsById);

  useEffect(() => {
    if (open && formikRef.current) {
      formikRef.current.resetForm({
        values: { batchIds: selectedTenantIds, ...INITIAL_VALUES },
      });
    }
    // Reset only on open transition; ignore mid-session selectedTenantIds changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleOpenChange(newOpen: boolean) {
    if (!newOpen) {
      tabbedFormRef.current?.resetTab();
      formikRef.current?.resetForm({
        values: { batchIds: [], ...INITIAL_VALUES },
      });
    }
    onOpenChange(newOpen);
  }

  async function handleSubmit(
    values: BulkCourriersFormValues,
    { setSubmitting, setStatus }: FormikHelpers<BulkCourriersFormValues>,
  ) {
    setStatus(undefined);
    try {
      const response = await bulkCreateCourrier({
        tenantIds: values.batchIds,
        courrierStaticId: values.courrierStaticId,
        courrierContent: values.courrierContent,
        typeEnvoi: values.typeEnvoi,
        files: values.files.map(({ src, filename }) => ({ src, filename })),
      });
      notifyBulkResults(
        response.results,
        ({ successCount, failureCount, total }) => {
          const s = successCount > 1 ? "s" : "";
          const sf = failureCount > 1 ? "s" : "";
          const envoiSuffix =
            values.typeEnvoi === "mail"
              ? " et envoyé par mail"
              : values.typeEnvoi === "lrar"
              ? " et envoyé en recommandé"
              : "";
          return {
            successSection: `Courrier${s} généré${s}${envoiSuffix}`,
            failureSection: `Courrier${sf} non généré${sf}`,
            successToastTitle:
              failureCount === 0
                ? `${successCount} courrier${s} généré${s}`
                : `${successCount} sur ${total} générés`,
            errorToastTitle: "Aucun courrier généré",
          };
        },
      );
      resetSelection?.();
      handleOpenChange(false);
    } catch (e) {
      setStatus(handleAPIError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Formik<BulkCourriersFormValues>
      innerRef={formikRef}
      initialValues={{ batchIds: selectedTenantIds, ...INITIAL_VALUES }}
      onSubmit={handleSubmit}
    >
      <TabbedForm id="bulk-courriers" ref={tabbedFormRef}>
        <ModalDialog
          open={open}
          onOpenChange={handleOpenChange}
          size="xl"
          tabsHeader={
            <TabbedForm.TabList>
              <TabbedForm.Tab panelId="type">Type de courrier</TabbedForm.Tab>
              <TabbedForm.Tab panelId="form">Contenu</TabbedForm.Tab>
              <TabbedForm.Tab panelId="preview">Aperçus</TabbedForm.Tab>
              <TabbedForm.Tab panelId="envoi">Envoi</TabbedForm.Tab>
            </TabbedForm.TabList>
          }
        >
          <Form className={styles.modalBody}>
            <TabbedForm.TabPanel id="type">
              <TypePanel />
              <ActionsWithStatus />
            </TabbedForm.TabPanel>

            <TabbedForm.TabPanel id="form">
              <FormPanel />
              <ActionsWithStatus />
            </TabbedForm.TabPanel>

            <TabbedForm.TabPanel id="preview">
              <PreviewPanel tenantsById={tenantsById} />
              <ActionsWithStatus />
            </TabbedForm.TabPanel>

            <TabbedForm.TabPanel id="envoi">
              <EnvoiPanel tenantsById={tenantsById} />
              <ActionsWithStatus />
            </TabbedForm.TabPanel>
          </Form>
        </ModalDialog>
      </TabbedForm>
    </Formik>
  );
}

function ActionsWithStatus() {
  const { status } = useFormikContext<BulkCourriersFormValues>();
  return (
    <div className={styles.actions}>
      <TabbedForm.Actions error={status} />
    </div>
  );
}

function useAllCourriers(): Courrier[] {
  const { courriers: courriersGeneriques }: { courriers: Courrier[] } =
    useCourriers("courrier");
  const { courriers: avenants }: { courriers: Courrier[] } =
    useCourriers("avenant");
  const { courriers: conges }: { courriers: Courrier[] } =
    useCourriers("conge");
  return useMemo<Courrier[]>(
    () => [...courriersGeneriques, ...avenants, ...conges],
    [courriersGeneriques, avenants, conges],
  );
}

function TypePanel() {
  const { values, setFieldValue } = useFormikContext<BulkCourriersFormValues>();
  const allCourriers = useAllCourriers();

  const handleSelect = useCallback(
    (staticId: string) => {
      const courrier = allCourriers.find((c) => c.static_id === staticId);
      if (!courrier) return;
      if (values.courrierStaticId !== courrier.static_id) {
        setFieldValue("courrierStaticId", courrier.static_id);
        setFieldValue(
          "courrierContent",
          wrapVariablesAsPills(courrier.content),
        );
      }
    },
    [allCourriers, values.courrierStaticId, setFieldValue],
  );

  return (
    <div className={styles.typeCatalog}>
      <Text color="muted">
        Choisissez le type de courrier à envoyer à tous les locataires
        sélectionnés.
      </Text>
      <SelectCourrier
        type="courrier"
        onSelectCourrier={handleSelect}
        massOnly
      />
      <SelectCourrier type="avenant" onSelectCourrier={handleSelect} massOnly />
      <SelectCourrier type="conge" onSelectCourrier={handleSelect} massOnly />
    </div>
  );
}

function FormPanel() {
  const { values, setFieldValue } = useFormikContext<BulkCourriersFormValues>();
  const editorRef = useRef<EditorHandle>(null);
  const allCourriers = useAllCourriers();

  const courrier = useMemo(
    () =>
      allCourriers.find((c) => c.static_id === values.courrierStaticId) ?? null,
    [allCourriers, values.courrierStaticId],
  );

  const availableVariables = useMemo(
    () => (courrier ? getCourrierVariables(courrier.content) : []),
    [courrier],
  );

  if (!courrier) {
    return (
      <Callout variant="info">
        Sélectionnez d&apos;abord un type de courrier dans l&apos;onglet
        précédent.
      </Callout>
    );
  }

  return (
    <div className={styles.editorWrapper}>
      <Text color="muted">
        Le contenu ci-dessous sera envoyé à tous les locataires sélectionnés.
        Les variables seront remplacées par les valeurs propres à chaque
        locataire au moment de l&apos;envoi.
      </Text>
      <VariablePicker
        available={availableVariables}
        label="Variables disponibles"
        onInsert={(key: string) => editorRef.current?.insertVariable(key)}
      />
      <Editor
        key={courrier.static_id}
        ref={editorRef}
        defaultValue={values.courrierContent}
        onChange={(html) => setFieldValue("courrierContent", html)}
        availableVariables={availableVariables}
        showVariablePicker={false}
      />
      <div className={styles.attachments}>
        <Text weight="bold">Pièces jointes</Text>
        <Text color="muted">
          Ajoutées à la suite de chaque courrier. En recommandé, le pli ne peut
          pas dépasser 10 pages au total.
        </Text>
        <FileInput multiple path="files" text="Déposez vos pièces jointes" />
      </div>
    </div>
  );
}

interface SendPanelProps {
  tenantsById: Record<number, Tenant>;
}

function getMainProfiles(tenant: Tenant): Profile[] {
  return tenant.profiles.filter((p) => p.garantor_of == null && p.status === 1);
}

function PreviewPanel({ tenantsById }: SendPanelProps) {
  const { values } = useFormikContext<BulkCourriersFormValues>();
  const {
    previewCourrierPdf,
  }: {
    previewCourrierPdf: (params: {
      tenantId: number;
      courrierStaticId: string;
      courrierContent: string;
    }) => Promise<string>;
  } = useTenants({ withById: true });

  const { batchTenants, activeTenantId, setActiveTenantId, removeFromBatch } =
    useBatchSelection(tenantsById);

  const contentHash = useMemo(() => {
    let h = 0;
    for (let i = 0; i < values.courrierContent.length; i++) {
      h = (h << 5) - h + values.courrierContent.charCodeAt(i);
      h |= 0;
    }
    return h.toString(36);
  }, [values.courrierContent]);

  const activeKey =
    activeTenantId !== null
      ? `${activeTenantId}-${values.courrierStaticId}-${contentHash}`
      : null;

  const fetcher = useCallback(
    ({
      tenantId,
      courrierStaticId: sid,
      courrierContent: content,
    }: {
      tenantId: number;
      courrierStaticId: string;
      courrierContent: string;
    }) =>
      previewCourrierPdf({
        tenantId,
        courrierStaticId: sid,
        courrierContent: content,
      }),
    [previewCourrierPdf],
  );

  const { previewUrl, previewLoading, previewError } = usePdfPreviewCache({
    activeKey,
    activePayload:
      activeTenantId !== null
        ? {
            tenantId: activeTenantId,
            courrierStaticId: values.courrierStaticId,
            courrierContent: values.courrierContent,
          }
        : null,
    fetcher,
    enabled: values.courrierStaticId !== "",
  });

  if (!values.courrierStaticId) {
    return (
      <Callout variant="info">
        Sélectionnez d&apos;abord un type de courrier dans l&apos;onglet
        précédent.
      </Callout>
    );
  }

  const attachmentsCount = values.files.length;

  return (
    <>
      {attachmentsCount > 0 && (
        <Callout variant="info">
          {attachmentsCount > 1
            ? `Les ${attachmentsCount} pièces jointes seront ajoutées à la suite de chaque courrier ; elles n'apparaissent pas dans cet aperçu.`
            : "La pièce jointe sera ajoutée à la suite de chaque courrier ; elle n'apparaît pas dans cet aperçu."}
        </Callout>
      )}
      <div className={bulkStyles.split}>
        <div className={bulkStyles.cardList}>
          {batchTenants.length === 0 && (
            <Text color="muted">Aucun locataire sélectionné.</Text>
          )}
          {batchTenants.map((tenant) => (
            <BulkSelectionCard
              key={tenant.id}
              fileName={`${values.courrierStaticId}.pdf`}
              tenantName={tenant.nom}
              isActive={tenant.id === activeTenantId}
              isIneligible={false}
              onSelect={() => setActiveTenantId(tenant.id)}
              onRemove={() => removeFromBatch(tenant.id)}
            />
          ))}
        </div>

        <BulkPdfPreview
          iframeTitle="Aperçu courrier"
          hasSelection={activeTenantId !== null}
          isIneligible={false}
          ineligibleTitle=""
          previewUrl={previewUrl}
          previewLoading={previewLoading}
          previewError={previewError}
        />
      </div>
    </>
  );
}

function EnvoiPanel({ tenantsById }: SendPanelProps) {
  const { values } = useFormikContext<BulkCourriersFormValues>();
  const { propertiesById }: { propertiesById: Record<number, Property> } =
    useProperties({ withById: true });
  const {
    profileBailleursById,
  }: {
    profileBailleursById: Record<number, ProfileBailleur>;
  } = useProfileBailleurs({ withById: true });
  const { account, refresh: refreshAccount } = useAccount();

  const { batchTenants } = useBatchSelection(tenantsById);

  const lrarGroups = useMemo<SenderGroup[]>(() => {
    const map = new Map<string, SenderGroup>();
    for (const tenant of batchTenants) {
      const property = propertiesById[tenant.id_bien];
      const bailleur = property?.id_profile_nom_owner
        ? profileBailleursById[property.id_profile_nom_owner]
        : null;
      const sender = bailleur?.profiles?.[0] ?? null;
      const key = sender ? `s${sender.id}` : "none";
      let group = map.get(key);
      if (!group) {
        group = { sender, tenants: [] };
        map.set(key, group);
      }
      group.tenants.push(tenant);
    }
    return Array.from(map.values());
  }, [batchTenants, propertiesById, profileBailleursById]);

  if (!values.courrierStaticId) {
    return (
      <Callout variant="info">
        Sélectionnez d&apos;abord un type de courrier dans l&apos;onglet
        précédent.
      </Callout>
    );
  }

  const totalLrarCost = batchTenants.length * RECOMMANDE_PRICE;
  const lrarInsufficient = (account?.credits ?? 0) < totalLrarCost;

  return (
    <div className={styles.envoiRow}>
      <RadioGroup styling="button" className={styles.envoiOptions}>
        <Radio
          name="typeEnvoi"
          value="lrar"
          label={
            <>
              <Lrar />
              <Text color="primary">Lettre recommandée</Text>
            </>
          }
        />
        <Radio
          name="typeEnvoi"
          value="mail"
          label={
            <>
              <SendMail />
              <Text color="primary">Envoyer par mail</Text>
            </>
          }
        />
        <Radio
          name="typeEnvoi"
          value="generer"
          label={
            <>
              <GenerateOnly />
              <Text color="primary">Créer sans envoi</Text>
            </>
          }
        />
      </RadioGroup>

      <Card padding="md">
        {values.typeEnvoi === "mail" && (
          <MailRecipients tenants={batchTenants} />
        )}

        {values.typeEnvoi === "lrar" && (
          <>
            <BuyCredits
              account={account}
              refreshAccount={refreshAccount}
              buyCredit={lrarInsufficient}
              typeCredits={ProductType.LRAR}
            />
            <Text color="muted">
              {batchTenants.length} courrier
              {batchTenants.length > 1 ? "s" : ""} recommandé
              {batchTenants.length > 1 ? "s" : ""} —{" "}
              {currencyFormatter(RECOMMANDE_PRICE / 100)} TTC par envoi, soit{" "}
              {currencyFormatter(totalLrarCost / 100)} TTC au total.
            </Text>
            <LrarRecipients groups={lrarGroups} />
          </>
        )}

        {values.typeEnvoi === "generer" && (
          <Text color="muted">
            Les courriers seront créés et disponibles dans les documents de
            chaque locataire, sans envoi automatique.
          </Text>
        )}
      </Card>
    </div>
  );
}

interface RecipientsProps {
  tenants: Tenant[];
}

function MailRecipients({ tenants }: RecipientsProps) {
  if (tenants.length === 0) {
    return <Text color="muted">Aucun locataire sélectionné.</Text>;
  }
  return (
    <div className={styles.recipientList}>
      {tenants.map((tenant) => {
        const mails = getMainProfiles(tenant)
          .map((p) => p.mail)
          .filter((m): m is string => Boolean(m));
        return (
          <Card key={tenant.id} padding="sm">
            <Text weight="bold">{tenant.nom}</Text>
            {mails.length > 0 ? (
              <Text variant="deci">{mails.join(", ")}</Text>
            ) : (
              <Text variant="deci" color="danger">
                Aucune adresse mail
              </Text>
            )}
          </Card>
        );
      })}
    </div>
  );
}

interface SenderGroup {
  sender: Profile | null;
  tenants: Tenant[];
}

interface LrarRecipientsProps {
  groups: SenderGroup[];
}

function ProfileLines({ profile }: { profile: Profile }) {
  const name =
    profile.type === 1
      ? `${profile.prenom ?? ""} ${profile.nom ?? ""}`.trim()
      : profile.nom_societe ?? "";
  return (
    <div className={styles.profileLines}>
      <Text variant="deci">{name}</Text>
      {profile.complement_adresse && (
        <Text variant="deci">{profile.complement_adresse}</Text>
      )}
      {profile.adresse && <Text variant="deci">{profile.adresse}</Text>}
      <Text variant="deci">
        {[profile.cp, profile.ville].filter(Boolean).join(" ")}
      </Text>
    </div>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <Text variant="deci" weight="bold" className={styles.section}>
      {children}
    </Text>
  );
}

function LrarRecipients({ groups }: LrarRecipientsProps) {
  if (groups.length === 0) {
    return <Text color="muted">Aucun locataire sélectionné.</Text>;
  }
  return (
    <div className={styles.senderGroups}>
      {groups.map((group, gi) => (
        <Card
          key={group.sender?.id ?? `none-${gi}`}
          padding="md"
          className={styles.senderGroupCard}
        >
          <div className={styles.senderHeader}>
            <SectionLabel>Expéditeur</SectionLabel>
            {group.sender ? (
              <ProfileLines profile={group.sender} />
            ) : (
              <Text variant="deci" color="danger">
                Aucun expéditeur
              </Text>
            )}
          </div>
          <SectionLabel>
            {group.tenants.length > 1
              ? `Destinataires (${group.tenants.length})`
              : "Destinataire"}
          </SectionLabel>
          <div className={styles.recipientItems}>
            {group.tenants.map((tenant) => {
              const recipient = getMainProfiles(tenant)[0];
              return (
                <div key={tenant.id} className={styles.recipientItem}>
                  <Text variant="deci" weight="bold">
                    {tenant.nom}
                  </Text>
                  {recipient ? (
                    <ProfileLines profile={recipient} />
                  ) : (
                    <Text variant="deci" color="danger">
                      Aucun destinataire actif
                    </Text>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      ))}
    </div>
  );
}
